import { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, FileText, Download, Check, CheckCheck } from 'lucide-react';
import { imgUrl } from '@/lib/api';
import { fmtDuration } from '@/hooks/useVoiceRecorder';

const fmtSize = (b) => (!b ? '' : b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function VoicePlayer({ src, duration, mine, testId }) {
  const audioRef = useRef();
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [current, setCurrent] = useState(0);
  const [dur, setDur] = useState(duration || 0);
  const bars = useMemo(() => Array.from({ length: 30 }, (_, i) => 0.25 + 0.75 * Math.abs(Math.sin(i * 1.7 + (src?.length || 3) * 0.37) * Math.cos(i * 0.45))), [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (a.paused) a.play().then(() => setPlaying(true)).catch(() => {}); else { a.pause(); setPlaying(false); }
  };
  const seek = (e) => {
    const a = audioRef.current;
    const r = e.currentTarget.getBoundingClientRect();
    const total = dur || a.duration;
    if (isFinite(total) && total > 0) a.currentTime = ((e.clientX - r.left) / r.width) * total;
  };

  return (
    <div className={`flex items-center gap-3 rounded-full pl-1.5 pr-3 py-1.5 min-w-[210px] ${mine ? 'bg-obsidian/10' : 'bg-white/5'}`} data-testid={testId}>
      <audio ref={audioRef} src={src} preload="metadata"
             onLoadedMetadata={(e) => { if (isFinite(e.target.duration)) setDur(e.target.duration); }}
             onTimeUpdate={(e) => { const t = dur || e.target.duration; setCurrent(e.target.currentTime); if (t) setProgress(e.target.currentTime / t); }}
             onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }}/>
      <button onClick={toggle} data-testid={`${testId}-toggle`} aria-label={playing ? 'Pausa' : 'Riproduci'}
              className={`w-8 h-8 rounded-full grid place-items-center shrink-0 transition-transform hover:scale-110 ${mine ? 'bg-obsidian text-chartreuse' : 'bg-chartreuse text-obsidian'}`}>
        {playing ? <Pause size={13} fill="currentColor"/> : <Play size={13} fill="currentColor" className="ml-0.5"/>}
      </button>
      <div className="flex items-center gap-[2px] h-7 flex-1 cursor-pointer" onClick={seek}>
        {bars.map((h, i) => {
          const on = i / bars.length < progress;
          return (
            <motion.span key={i} className="w-[3px] rounded-full"
                         animate={playing ? { scaleY: [1, 0.6 + h * 0.6, 1] } : { scaleY: 1 }}
                         transition={playing ? { repeat: Infinity, duration: 0.9, delay: i * 0.03 } : { duration: 0.2 }}
                         style={{ height: `${h * 100}%`, background: on ? (mine ? '#0b0b0b' : 'rgb(180,255,0)') : (mine ? 'rgba(11,11,11,0.28)' : 'rgba(240,245,230,0.25)') }}/>
          );
        })}
      </div>
      <span className="font-mono text-[11px] tabular-nums opacity-80">{fmtDuration(playing || current ? current : dur)}</span>
    </div>
  );
}

function PdfChip({ a, mine }) {
  return (
    <a href={imgUrl(a.file_id)} target="_blank" rel="noopener noreferrer" data-testid={`msg-pdf-${a.file_id}`}
       className={`flex items-center gap-3 rounded-xl p-2.5 pr-3 border transition-colors ${mine ? 'bg-obsidian/10 border-obsidian/15 hover:bg-obsidian/15' : 'bg-terracotta/10 border-terracotta/25 hover:bg-terracotta/15'}`}>
      <span className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${mine ? 'bg-obsidian text-chartreuse' : 'bg-terracotta text-white'}`}><FileText size={16}/></span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium truncate">{a.name || 'Documento.pdf'}</span>
        <span className="block text-[10px] uppercase tracking-widest opacity-60">PDF {fmtSize(a.size) && `· ${fmtSize(a.size)}`}</span>
      </span>
      <Download size={14} className="opacity-60 shrink-0"/>
    </a>
  );
}

function ImageGrid({ images }) {
  return (
    <div className={`grid gap-1 ${images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
      {images.map((a, i) => {
        const url = a.previewUrl || imgUrl(a.file_id);
        return (
          <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block overflow-hidden rounded-xl">
            <img src={url} alt="allegato" className="w-full max-h-56 object-cover transition-transform duration-500 hover:scale-105"/>
          </a>
        );
      })}
    </div>
  );
}

export function MessageBubble({ m, mine, highlighted }) {
  const metas = m.attachments_meta?.length ? m.attachments_meta : (m.attachments || []).map(fid => ({ file_id: fid, kind: 'image' }));
  const images = metas.filter(a => a.kind === 'image');
  const others = metas.filter(a => a.kind !== 'image');
  return (
    <motion.div layout="position" initial={{ opacity: 0, y: 12, scale: 0.94 }} animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ type: 'spring', damping: 22, stiffness: 320 }}
                id={`msg-${m.message_id}`} data-testid={`msg-${m.message_id}`}
                className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[82%] sm:max-w-[70%] rounded-2xl px-3.5 py-2.5 text-sm space-y-2 transition-shadow duration-700
                      ${mine ? 'bg-chartreuse text-obsidian rounded-br-md shadow-[0_8px_30px_-12px_rgba(180,255,0,0.55)]' : 'bg-secondary text-foreground border border-border rounded-bl-md'}
                      ${m._pending ? 'opacity-70' : ''} ${highlighted ? 'ring-2 ring-[#8dd8ff] shadow-[0_0_40px_rgba(141,216,255,0.45)]' : ''}`}>
        {images.length > 0 && <ImageGrid images={images}/>}
        {others.map(a => a.kind === 'audio'
          ? <VoicePlayer key={a.file_id} src={a.previewUrl || imgUrl(a.file_id)} duration={a.duration} mine={mine} testId={`msg-voice-${a.file_id}`}/>
          : <PdfChip key={a.file_id} a={a} mine={mine}/>)}
        {m.content && <div className="whitespace-pre-wrap break-words leading-relaxed">{m.content}</div>}
        <div className={`flex items-center justify-end gap-1 text-[10px] ${mine ? 'text-obsidian/60' : 'text-muted-foreground'}`}>
          {new Date(m.created_at).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
          {mine && (m._pending ? <span>· invio…</span>
            : m.read ? <CheckCheck size={13} className="text-sky-700" data-testid="msg-read-receipt"/> : <Check size={13}/>)}
        </div>
      </div>
    </motion.div>
  );
}

export function DaySeparator({ date }) {
  const d = new Date(date);
  const today = new Date();
  const y = new Date(); y.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  const label = same(d, today) ? 'Oggi' : same(d, y) ? 'Ieri' : d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  return (
    <div className="flex items-center gap-3 py-2">
      <span className="h-px flex-1 bg-border"/>
      <span className="text-[10px] uppercase tracking-[0.24em] font-bold text-muted-foreground">{label}</span>
      <span className="h-px flex-1 bg-border"/>
    </div>
  );
}

export function TypingBubble({ name }) {
  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} data-testid="typing-indicator"
                className="inline-flex items-center gap-1.5 rounded-2xl rounded-bl-md px-4 py-3 bg-secondary border border-border">
      {[0, 1, 2].map(i => (
        <motion.span key={i} className="w-1.5 h-1.5 rounded-full bg-chartreuse"
                     animate={{ y: [0, -4, 0] }} transition={{ repeat: Infinity, duration: 0.8, delay: i * 0.15 }}/>
      ))}
      <span className="ml-1 text-[11px] text-muted-foreground">{name} sta scrivendo…</span>
    </motion.div>
  );
}
